import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import { useHistory } from "react-router-dom"
import { buildSharedPageActions } from "@helpers/sharedPageActions"
import UserContext from "@context/UserContext"

const PageContext = React.createContext({
  pageContext: null,
  setPageContext: () => {},
  clearPageContext: () => {}
})

export const PageContextProvider = ({ children }) => {
  const [pageContext, setPageContextState] = useState(null)
  const history = useHistory()
  const { user } = useContext(UserContext)

  const setPageContext = useCallback((ctx) => {
    setPageContextState(ctx || null)
  }, [])

  const clearPageContext = useCallback(() => {
    setPageContextState(null)
  }, [])

  // Drop any lingering context when the route changes. Pages re-populate it
  // as they mount, so a navigation never carries the previous page's context
  // into the next message.
  useEffect(() => {
    const unlisten = history.listen(() => {
      setPageContextState(null)
    })
    return unlisten
  }, [history])

  // Handlers close over the stable `history.push`; rebuilt if the user changes so
  // staff search routes to the right page for the current new-placement-flow state.
  const sharedActions = useMemo(() => buildSharedPageActions(history, user), [history, user])

  const effectivePageContext = useMemo(() => {
    const pageActions = (pageContext && pageContext.actions) || {}
    const mergedActions = { ...sharedActions, ...pageActions }
    return {
      ...(pageContext || {}),
      actions: mergedActions
    }
  }, [pageContext, sharedActions])

  const value = useMemo(
    () => ({ pageContext: effectivePageContext, setPageContext, clearPageContext }),
    [effectivePageContext, setPageContext, clearPageContext]
  )

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>
}

PageContextProvider.propTypes = {
  children: PropTypes.node
}

export const usePageContext = () => useContext(PageContext)

// Declarative setter — pages render <PageContextSetter ... /> inside their
// existing tree to publish context. Works in both class and function pages.
//
// `actions` lets a page expose one-click operations to the Postcard Assistant.
// Each entry is `{ description, label?, handler }`. The description is the
// natural-language hint sent to Claude; the handler runs in-place when the user
// clicks the rendered button. Handlers are stripped at the JSON boundary inside
// the ChatWidget, so the AI only ever sees `{ description, label }`.
//
// `onRefresh` lets a page refetch its data when the assistant mutates
// something backend-side (resource_changed SSE event, PCD-447). Pass the
// page's existing data-fetch method; it receives the event
// `{ resourceType, action, toolName }` and may ignore types it doesn't show.
export const PageContextSetter = ({ resourceType, resourceId, pageTitle, url, data, actions, onRefresh }) => {
  const { setPageContext } = usePageContext()
  // Keys + description fingerprints — NOT handler identities, which would
  // churn on every render and cause useless republishes.
  const actionsKey = actions
    ? Object.keys(actions)
        .sort()
        .map((name) => `${name}:${actions[name] && actions[name].description}`)
        .join("|")
    : ""
  const key = JSON.stringify({ resourceType, resourceId, pageTitle, url, data, actionsKey })

  // Latest callback held in a ref, published as a stable wrapper — like action
  // handlers, identity churn must not retrigger the publish effect, but the
  // ChatWidget should always invoke the freshest closure.
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh
  const stableOnRefresh = useCallback((event) => {
    if (typeof onRefreshRef.current === "function") onRefreshRef.current(event)
  }, [])

  useEffect(() => {
    if (!resourceType && !pageTitle) return
    setPageContext({
      resourceType: resourceType || null,
      resourceId: resourceId == null || resourceId === "" ? null : String(resourceId),
      pageTitle: pageTitle || null,
      url: url || (typeof window !== "undefined" ? window.location.pathname : null),
      data: data || null,
      actions: actions || null,
      onRefresh: onRefresh ? stableOnRefresh : null
    })
    // key captures every serializable field; setPageContext and
    // stableOnRefresh are stable, and onRefresh presence only flips with `key`
    // in practice (pages pass it unconditionally).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, !!onRefresh])

  return null
}

PageContextSetter.propTypes = {
  resourceType: PropTypes.string,
  resourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pageTitle: PropTypes.string,
  url: PropTypes.string,
  data: PropTypes.object,
  actions: PropTypes.objectOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      label: PropTypes.string,
      handler: PropTypes.func.isRequired,
      // Optional JSONSchema describing parameters the AI must pass. Used to
      // register a richer Anthropic tool. Omit for plain no-argument actions.
      argSchema: PropTypes.object,
      // Optional builder for the rendered button label, given the args. Falls
      // back to `label` then to the action key.
      labelFor: PropTypes.func
    })
  ),
  onRefresh: PropTypes.func
}

export default PageContext
